import { fetch as fetchPolyfill } from "whatwg-fetch";
import { createIntegrationError, ERROR_TYPES } from "./integrationErrors";
import {
  REACT_APP_APIFY_URL,
  REACT_APP_PANEL_APP_REST_URL,
  REACT_APP_SECURE_EPAYCO_URL,
} from "../../config/env";

const DEFAULT_TIMEOUT_MS = 30000;

export function getDefaultTimeoutMs() {
  return DEFAULT_TIMEOUT_MS;
}

export function getGeoIpTimeoutMs() {
  return 15000;
}

export function getGeoIpUrl() {
  return `${REACT_APP_APIFY_URL}/getip`;
}

export function getDataTransactionUrl() {
  if (REACT_APP_PANEL_APP_REST_URL) {
    return `${REACT_APP_PANEL_APP_REST_URL}/email/transaccion`;
  }
  return `${REACT_APP_SECURE_EPAYCO_URL}/apprest/email/transaccion`;
}

export function getRouteEmailUrl() {
  return `${REACT_APP_PANEL_APP_REST_URL}/email/send`;
}

export function getSecureTimeoutMs() {
  return 30000;
}

export function fetchWithTimeout(
  url,
  options = {},
  timeoutMs = DEFAULT_TIMEOUT_MS,
) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  const fetchOptions = {
    ...options,
    signal: controller.signal,
  };

  return fetchPolyfill(url, fetchOptions)
    .then((response) => {
      clearTimeout(timeoutId);
      return response;
    })
    .catch((error) => {
      clearTimeout(timeoutId);
      if (error.name === "AbortError") {
        throw createIntegrationError(ERROR_TYPES.TIMEOUT, { endpoint: url });
      }
      throw createIntegrationError(ERROR_TYPES.NETWORK_ERROR, {
        endpoint: url,
        originalMessage: error.message,
      });
    });
}

export function assertHttpOk(response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  const errorType =
    response.status >= 500 ? ERROR_TYPES.HTTP_ERROR : ERROR_TYPES.HTTP_ERROR;

  throw createIntegrationError(errorType, {
    statusCode: response.status,
    endpoint: response.url,
  });
}

export async function parseJsonSafe(response) {
  const contentType = response.headers.get("content-type") || "";
  const text = await response.text();

  if (!text || !text.trim()) {
    throw createIntegrationError(ERROR_TYPES.EMPTY_RESPONSE, {
      statusCode: response.status,
      endpoint: response.url,
    });
  }

  if (
    !contentType.includes("application/json") &&
    text.trim().startsWith("<")
  ) {
    throw createIntegrationError(ERROR_TYPES.INVALID_JSON, {
      statusCode: response.status,
      endpoint: response.url,
    });
  }

  try {
    return JSON.parse(text);
  } catch (e) {
    throw createIntegrationError(ERROR_TYPES.INVALID_JSON, {
      statusCode: response.status,
      endpoint: response.url,
      originalMessage: e.message,
    });
  }
}

export function assertApiContract(data, options = {}) {
  const { requireSuccess = false, requireData = false } = options;

  if (data === null || data === undefined) {
    throw createIntegrationError(ERROR_TYPES.CONTRACT_ERROR, {
      backendMessage: null,
    });
  }

  if (typeof data !== "object") {
    throw createIntegrationError(ERROR_TYPES.CONTRACT_ERROR);
  }

  if (requireSuccess && typeof data.success !== "boolean") {
    throw createIntegrationError(ERROR_TYPES.CONTRACT_ERROR);
  }

  if (requireData && data.success === true && data.data === undefined) {
    throw createIntegrationError(ERROR_TYPES.CONTRACT_ERROR);
  }

  return data;
}

export async function fetchExternal(
  url,
  options = {},
  timeoutMs = DEFAULT_TIMEOUT_MS,
) {
  const response = await fetchWithTimeout(url, options, timeoutMs);
  assertHttpOk(response);
  return parseJsonSafe(response);
}

export async function fetchJson(
  url,
  options = {},
  timeoutMs = DEFAULT_TIMEOUT_MS,
) {
  const response = await fetchWithTimeout(url, options, timeoutMs);
  assertHttpOk(response);
  return parseJsonSafe(response);
}
